import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./providers/AuthProvider";
import { NotificationHandler } from "./services/notificationHandler";
import "./services/instantEmailFix";
import { SEOProvider } from "./providers/SEOProvider";


// Initialize email notification handler
NotificationHandler.initialize();
import PreloaderWrapper from "./components/PreloaderWrapper";
// Critical routes preloaded for instant navigation
import Index from "./pages/Index";
import Login from "./pages/Login";
// Other routes lazy loaded for balanced performance
import { Suspense, lazy } from "react";
const NotFound = lazy(() => import("./pages/NotFound"));
const Auctions = lazy(() => import("./pages/Auctions"));
const AuctionDetail = lazy(() => import("./pages/AuctionDetail"));
const Artworks = lazy(() => import("./pages/Artworks"));
const ArtworkDetail = lazy(() => import("./pages/ArtworkDetail"));
const Artists = lazy(() => import("./pages/Artists"));
const ArtistDetail = lazy(() => import("./pages/ArtistDetail"));
const Profile = lazy(() => import("./pages/Profile"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminChoice = lazy(() => import("./pages/AdminChoice"));
const AdminMini = lazy(() => import("./pages/AdminMini"));
const EdwardsListPage = lazy(() => import("./pages/EdwardsList"));
const UpdatesPage = lazy(() => import("./pages/Updates"));
const AdvancedControl = lazy(() => import("./pages/AdvancedControl"));
const Consignment = lazy(() => import("./pages/Consignment"));
const Broadcast = lazy(() => import("./pages/Broadcast"));
const BroadcastAdmin = lazy(() => import("./pages/BroadcastAdmin"));
const LiveBroadcast = lazy(() => import("./pages/LiveBroadcast"));
const Presentation = lazy(() => import("./pages/Presentation"));
const AuctionPresentation = lazy(() => import("./pages/AuctionPresentation"));
const Contact = lazy(() => import("./pages/Contact"));
const ContactForm = lazy(() => import("./pages/ContactForm"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Cookies = lazy(() => import("./pages/Cookies"));
const DashboardSettings = lazy(() => import("./pages/dashboard/Settings"));
import ChatLauncher from "./components/chat/ChatLauncher";
import Appraisal from "./pages/Appraisal";
import Sitemap from "./pages/Sitemap";
import FAQs from "./pages/FAQs";
import FloridaHighwaymen from "./pages/FloridaHighwaymen";
import CollectingGuide from "./pages/CollectingGuide";
import AppraisalGuide from "./pages/AppraisalGuide";
import EditorialPolicy from "./pages/EditorialPolicy";
import Reviews from "./pages/Reviews";
import RouteCanonicalizer from "./components/RouteCanonicalizer";

const queryClient = new QueryClient();

const App = () => {
	return (
		<React.StrictMode>
			<QueryClientProvider client={queryClient}>
				<TooltipProvider>
					<Toaster />
					<Sonner />
					<BrowserRouter>
						<AuthProvider>
							<SEOProvider>
                                <PreloaderWrapper>
									<RouteCanonicalizer />
                                    <Suspense fallback={<div />}>
                                    <Routes>
										<Route path="/" element={<Index />} />
										<Route path="/login" element={<Login />} />
										<Route path="/dashboard/settings" element={<DashboardSettings />} />
										<Route path="/auctions" element={<Auctions />} />
										<Route path="/auctions/:id" element={<AuctionDetail />} />
										<Route path="/artworks" element={<Artworks />} />
										<Route path="/artworks/:id" element={<ArtworkDetail />} />
										<Route path="/artists" element={<Artists />} />
										<Route path="/artists/:id" element={<ArtistDetail />} />
										<Route path="/profile" element={<Profile />} />
										<Route path="/admin" element={<AdminChoice />} />
										<Route path="/admin/dashboard" element={<Admin />} />
										<Route path="/admin-mini" element={<AdminMini />} />
										<Route path="/admin/advanced-control" element={<AdvancedControl />} />
										<Route path="/consign" element={<Consignment />} />
									<Route path="/live-broadcast" element={<LiveBroadcast />} />
									<Route path="/broadcast" element={<Broadcast />} />
									<Route path="/broadcast-admin" element={<BroadcastAdmin />} />
									<Route path="/auction-presentation/:auctionId" element={<AuctionPresentation />} />
									<Route path="/presentation" element={<Presentation />} />
									<Route path="/edwards-list" element={<EdwardsListPage />} />
									<Route path="/updates" element={<UpdatesPage />} />
										<Route path="/contact" element={<Contact />} />
										<Route path="/contact-form" element={<ContactForm />} />
										<Route path="/blog" element={<Blog />} />
										<Route path="/blog/:slug" element={<BlogPost />} />
										<Route path="/privacy" element={<Privacy />} />
										<Route path="/terms" element={<Terms />} />
										<Route path="/cookies" element={<Cookies />} />
										<Route path="/appraisal" element={<Appraisal />} />
										<Route path="/sitemap" element={<Sitemap />} />
                  <Route path="/faq" element={<Navigate to="/faqs" replace />} />
										<Route path="/faqs" element={<FAQs />} />
                  <Route path="/florida-highwaymen" element={<FloridaHighwaymen />} />
                  <Route path="/collecting-florida-highwaymen-art" element={<CollectingGuide />} />
                  <Route path="/florida-highwaymen-appraisal" element={<AppraisalGuide />} />
                  <Route path="/editorial-policy" element={<EditorialPolicy />} />
                  <Route path="/reviews" element={<Reviews />} />
										<Route path="*" element={<NotFound />} />
                                    </Routes>
                                    </Suspense>
									{/* Global floating chat launcher */}
									<ChatLauncher />
								</PreloaderWrapper>
							</SEOProvider>
						</AuthProvider>
					</BrowserRouter>
				</TooltipProvider>
			</QueryClientProvider>
		</React.StrictMode>
	);
};

export default App;
